export interface TemplatePropSource {
  nodeId: string;
  fieldPath: string;
  /** Display label (humanized). Do NOT use for lookup — pass nodeTypes map instead. */
  nodeLabel?: string;
}

export interface ActionSchemaLookup {
  [actionType: string]: {
    payloadSchema?: {
      properties?: Record<string, { enum?: unknown[]; type?: string }>;
    };
  };
}

/**
 * Resolve the allowed chain IDs for a template prop by inspecting its
 * `sources` — each source points at a node field in the workflow.
 *
 * For every source whose node resolves to an action type (via `nodeTypes`
 * map keyed by nodeId) that has a numeric chainId enum in its payload
 * schema, we intersect the enums. Built-in / logic / trigger nodes have
 * no entry in `actionByType` so they are skipped naturally.
 *
 * Returns undefined when no action source carries a numeric enum — caller
 * then falls back to showing all chains.
 *
 * Field path limitation: this helper performs a flat lookup
 * (`properties[fieldPath]`) and does not traverse nested schema properties.
 * All current chainId fields in action payloadSchemas are top-level, so this
 * is sufficient. If an action ever nests chainId (e.g. `config.chainId`),
 * either flatten the schema or extend this helper to parse dotted paths.
 *
 * @param sources     The template prop's sources declaration.
 * @param actionByType  Map of actionType -> action schema (from `useActions()`).
 * @param nodeTypes   Map of nodeId -> actionType, derived from workflow
 *                    definition's `nodes` record. REQUIRED for correct
 *                    lookup — do not rely on `source.nodeLabel` which may
 *                    be a humanized display string.
 */
export function resolveAllowedChainIdsFromSources(
  sources: TemplatePropSource[] | undefined,
  actionByType: ActionSchemaLookup,
  nodeTypes: Record<string, string>,
): number[] | undefined {
  if (!Array.isArray(sources) || sources.length === 0) return undefined;

  const enums: number[][] = [];
  for (const s of sources) {
    const actionType = nodeTypes[s.nodeId];
    if (!actionType) continue;

    const action = actionByType[actionType];
    const field = action?.payloadSchema?.properties?.[s.fieldPath];
    const rawEnum = field?.enum;
    if (Array.isArray(rawEnum) && rawEnum.length > 0 && rawEnum.every(v => typeof v === "number")) {
      enums.push(rawEnum as number[]);
    }
  }

  if (enums.length === 0) return undefined;

  const [first, ...rest] = enums;
  return first.filter(id => rest.every(e => e.includes(id)));
}
